import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// The document template with {{tags}} — kept as fallback
const TEMPLATE = `<div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px 20px; line-height: 1.6;">
  <div style="text-align: center; margin-bottom: 30px;">
    <div style="font-weight: bold; font-size: 1.1em;">Discovery Records, Inc.</div>
    <div>www.discoveryrecordsinc.com</div>
    <div>90 S. Main Street, Suite 108 &bull; Grapevine, Texas 76051</div>
    <div>Ph. 817-424-3672 &bull; Fax 817-424-3692 &bull; Toll Free 866-4MEDRE C</div>
  </div>
  <div style="margin-bottom: 20px;">{{OrderDate}}</div>
  <div style="text-align: center; font-size: 1.2em; font-weight: bold; margin-bottom: 20px;">ORDER CONFIRMATION</div>
  <div style="margin-bottom: 20px;">
    {{OrderedBy}}<br/>
    {{AT_FirstName}} {{AT_LastName}}<br/>
    {{AT_FirmName}}<br/>
    Bar No. {{AT_Bar}}<br/>
    {{AT_Address}}<br/>
    {{AT_Address2}}<br/>
    {{AT_City}}, {{AT_State}} {{AT_ZipCode}}
  </div>
  <div>File No. {{FileNumber}}&nbsp;&nbsp;&nbsp;&nbsp;Cause No. {{CauseNumber}}</div>
  <div style="margin: 10px 0;">{{Style1}} VS {{Style2}}</div>
  <div style="margin-bottom: 20px;">IN THE {{TypeOfCase}} COURT {{County}} COUNTY, TEXAS {{CourtNumber}}</div>
  <div style="margin-bottom: 15px;">
    <strong>Attorneys:</strong><br/>
    {{AT2_Block}}
    {{AT3_Block}}
    {{AT4_Block}}
    {{AT5_Block}}
  </div>
  <div style="margin-bottom: 10px;"><strong>Re:</strong> {{PT_Name}}</div>
  <div style="margin-bottom: 5px;">D.O.B.: {{PT_Birthdate}}&nbsp;&nbsp;&nbsp;SSN: {{PT_SSN}}</div>
  <div style="margin: 20px 0;">
    <strong>Direct Bill Information:</strong><br/>
    {{Adjuster_Name}}<br/>
    {{INS_Company}}<br/>
    Claim # {{Claim_Number}}<br/>
    {{INS_Address}}<br/>
    Date of Loss: {{DOL}}<br/>
    {{INS_City_St_zip}}
  </div>
  <div style="margin-bottom: 10px;">Obtain Records by: {{obtainby}}</div>
  <div style="margin-bottom: 10px;"><strong>Record Locations:</strong></div>
  {{WORK_ORDERS_SECTION}}
</div>`;

function fillTemplate(template, cause) {
  const attorneys = cause.attorneys || [];
  const clientAtty = attorneys.find(a => a.type === 'Client') || {};
  const opposingAtty = attorneys.find(a => a.type === 'Opposing') || {};
  const additionalAttys = attorneys.filter(a => a.type !== 'Client' && a.type !== 'Opposing');

  const attyBlock = (a) => {
    if (!a || !a.attorney_name) return '';
    return `${a.attorney_name}<br/>${a.firm_name || ''}<br/>${a.address || ''}<br/>${a.phone || ''}<br/>`;
  };

  const workOrdersHtml = (cause.work_orders || []).map(wo => `
    <div style="margin-bottom: 10px; padding: 8px; border-left: 3px solid #1a2744;">
      <strong>Type:</strong> ${wo.stop_location_type_records || wo.type_of_record || ''}<br/>
      ${wo.stop_location_name || ''}<br/>
      ${wo.stop_location_add1_name ? wo.stop_location_add1_name + '<br/>' : ''}
      ${wo.stop_location_address || ''} ${wo.stop_location_addr2 || ''}<br/>
      ${wo.stop_location_city_state_zip || ''}<br/>
      ${wo.stop_location_phone ? 'Ph: ' + wo.stop_location_phone : ''} ${wo.stop_location_fax ? 'Fax: ' + wo.stop_location_fax : ''}
    </div>
  `).join('');

  const fields = {
    OrderDate: cause.order_date || '',
    OrderedBy: cause.ordered_by || '',
    FileNumber: cause.file_number || '',
    CauseNumber: cause.cause_number || '',
    Style1: cause.style_plaintiff || '',
    Style2: cause.style_defendant || '',
    TypeOfCase: cause.type_of_case || '',
    County: cause.county || '',
    CourtNumber: cause.court_number || '',
    PT_Name: cause.patient_name || '',
    PT_Birthdate: cause.patient_dob || '',
    PT_SSN: cause.patient_ssn ? '***-**-' + String(cause.patient_ssn).slice(-4) : '',
    Adjuster_Name: cause.adjuster_name || '',
    INS_Company: cause.insurance_company || '',
    Claim_Number: cause.claim_number || '',
    INS_Address: cause.insurance_address || '',
    DOL: cause.date_of_loss || '',
    INS_City_St_zip: '',
    obtainby: cause.obtain_records_by || '',
    AT_FirstName: clientAtty.attorney_name ? clientAtty.attorney_name.split(' ')[0] : '',
    AT_LastName: clientAtty.attorney_name ? clientAtty.attorney_name.split(' ').slice(1).join(' ') : '',
    AT_FirmName: clientAtty.firm_name || '',
    AT_Bar: clientAtty.bar_number || '',
    AT_Address: clientAtty.address || '',
    AT_Address2: clientAtty.addr2 || '',
    AT_City: clientAtty.city || '',
    AT_State: clientAtty.state_code || '',
    AT_ZipCode: clientAtty.zip_code || '',
    AT2_Block: attyBlock(opposingAtty),
    AT3_Block: attyBlock(additionalAttys[0]),
    AT4_Block: attyBlock(additionalAttys[1]),
    AT5_Block: attyBlock(additionalAttys[2]),
    WORK_ORDERS_SECTION: workOrdersHtml || '<div style="color: #999;">No work orders</div>',
  };

  let result = template;
  Object.entries(fields).forEach(([key, val]) => {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val || '');
  });
  return result;
}

export default function MergeDocument({ causeId, onClose, invoiceMode = false }) {
  const [mergeData, setMergeData] = useState(null);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  // WO picker state
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [causeWorkOrders, setCauseWorkOrders] = useState([]);
  const [selectedWOs, setSelectedWOs] = useState([]);  // selected wo_numbers
  const [showWOPicker, setShowWOPicker] = useState(false);
  const [perWOMode, setPerWOMode] = useState(true); // default: one render per WO, concatenated
  const token = localStorage.getItem('token');

  // On mount, load available templates + work orders for this cause
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const [tRes, woRes] = await Promise.all([
          axios.get(`${API_URL}/api/templates`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/causes/${causeId}`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setAvailableTemplates(tRes.data);
        const wos = (woRes.data.work_orders || []).filter(wo => wo.stop_location_name);
        setCauseWorkOrders(wos);
        setSelectedWOs(wos.map(wo => wo.wo_number));  // default: all selected
      } catch (err) {
        console.error('Failed to load templates', err);
      }
      setLoadingTemplates(false);
    };
    loadTemplates();
  }, [causeId]);

  // Step 1: user clicks a template → check for existing invoices, then show WO picker
  const handleTemplateClick = async (template) => {
    // Check for existing invoices on selected WOs
    if (causeWorkOrders.length > 0) {
      try {
        const checks = await Promise.all(
          causeWorkOrders.map(wo =>
            axios.get(`${API_URL}/api/work-orders/${wo.id}/invoice-exists`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => ({ wo, ...r.data }))
          )
        );
        const existing = checks.filter(c => c.has_invoice);
        if (existing.length > 0) {
          const names = existing.map(c => `${c.wo.wo_number} (${c.count} invoice${c.count>1?'s':''})`).join(', ');
          const proceed = window.confirm(
            `⚠️ The following work orders already have invoices:\n\n${names}\n\nMerging again will create a new duplicate invoice. Proceed?`
          );
          if (!proceed) return;
        }
      } catch (e) { /* ignore check errors */ }
    }
    if (causeWorkOrders.length > 1) {
      setPendingTemplate(template);
      setShowWOPicker(true);
    } else {
      doRender(template, selectedWOs);
    }
  };

  // Step 2: after WO selection confirmed
  const confirmWOSelection = () => {
    setShowWOPicker(false);
    doRender(pendingTemplate, selectedWOs, perWOMode);
    setPendingTemplate(null);
  };

  const toggleWO = (woNum) => {
    setSelectedWOs(prev =>
      prev.includes(woNum) ? prev.filter(n => n !== woNum) : [...prev, woNum]
    );
  };

  // Step 3: actually call render API and download
  const doRender = async (template, woNums, perWo = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (woNums && woNums.length > 0) params.set('wo_numbers', woNums.join(','));
      if (perWo && woNums && woNums.length > 1) params.set('per_wo', 'true');
      if (invoiceMode) params.set('invoice_mode', 'true');
      const woParam = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`${API_URL}/api/templates/${template.id}/render/${causeId}${woParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to render document');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document_${causeId}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
      // Read invoice numbers from response headers
      const invNums = res.headers.get('X-Invoice-Numbers') || '';
      const invNote = invNums ? `<p style="color:#1a2744;font-weight:600;margin-top:12px">📋 Invoice${invNums.includes(',') ? 's' : ''} saved: <span style="font-family:monospace">${invNums}</span></p>` : '';
      setSelectedTemplate(template);
      setHtml(`<div style="text-align:center;padding:60px"><div style="font-size:3em">✅</div><h2>Document Downloaded</h2><p>Your Word document has been generated and downloaded.</p><p style="color:#666">Check your Downloads folder for the .docx file.</p>${invNote}</div>`);
    } catch (err) {
      console.error(err);
      alert('Failed to generate document: ' + err.message);
      setSelectedTemplate(template);
      setHtml('<p style="color:red">Failed to load merge data</p>');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${selectedTemplate?.name || 'Document'}</title></head><body>${html}</body></html>`);
    win.document.close();
    win.print();
  };

  // WO picker modal
  const WOPickerModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-800">Select Work Orders to Include</h3>
          <button onClick={() => { setShowWOPicker(false); setPendingTemplate(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>
        <div className="px-5 py-4">
          <p className="text-sm text-gray-500 mb-2">Choose which work orders to include in this merge document:</p>
          {/* Per-WO mode toggle */}
          <div className="flex items-center gap-2 mb-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
            <input type="checkbox" id="perWoMode" checked={perWOMode} onChange={e => setPerWOMode(e.target.checked)} className="w-4 h-4 accent-blue-600" />
            <label htmlFor="perWoMode" className="text-sm text-blue-800 cursor-pointer">
              <span className="font-medium">Merge per Work Order</span>
              <span className="text-blue-600 ml-1 text-xs">(renders each WO separately, concatenates into one doc)</span>
            </label>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {causeWorkOrders.map(wo => (
              <label key={wo.wo_number} className="flex items-start gap-3 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedWOs.includes(wo.wo_number)}
                  onChange={() => toggleWO(wo.wo_number)}
                  className="mt-0.5 w-4 h-4 accent-blue-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-800">{wo.wo_number}</div>
                  <div className="text-xs text-gray-500 truncate">{wo.stop_location_name}</div>
                  {wo.stop_location_address && <div className="text-xs text-gray-400 truncate">{wo.stop_location_address}</div>}
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => setSelectedWOs(causeWorkOrders.map(w => w.wo_number))} className="text-xs text-blue-600 hover:underline">Select all</button>
            <span className="text-gray-300">·</span>
            <button onClick={() => setSelectedWOs([])} className="text-xs text-blue-600 hover:underline">Clear all</button>
          </div>
        </div>
        <div className="flex gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={confirmWOSelection}
            disabled={selectedWOs.length === 0}
            className="flex-1 px-4 py-2 text-white rounded text-sm disabled:opacity-50"
            style={{ backgroundColor: '#1a2744' }}
          >
            Merge {selectedWOs.length} Work Order{selectedWOs.length !== 1 ? 's' : ''}
          </button>
          <button onClick={() => { setShowWOPicker(false); setPendingTemplate(null); }} className="px-4 py-2 bg-gray-200 rounded text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );

  // Template picker screen
  if (!selectedTemplate) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex flex-col">
        {showWOPicker && <WOPickerModal />}
        <div className="bg-white flex items-center justify-between px-4 py-2 border-b shadow">
          <h2 className="font-bold text-lg" style={{color:'#1a2744'}}>{invoiceMode ? '💳 Generate Invoice — Select Template' : '📄 Select Template'}</h2>
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-gray-200 rounded">✕ Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-gray-500 mt-20">Generating document...</div>
          ) : loadingTemplates ? (
            <div className="text-center text-gray-500 mt-20">Loading templates...</div>
          ) : (
            <div className="max-w-3xl mx-auto">
              {['Order Confirmation', 'Subpoena', 'Authorization', 'Invoice', 'Other'].map(type => {
                const typeTemplates = availableTemplates.filter(t => t.document_type === type);
                if (typeTemplates.length === 0) return null;
                return (
                  <div key={type} className="mb-6">
                    <h3 className="font-semibold text-gray-700 mb-3 border-b pb-1">{type}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {typeTemplates.map(t => (
                        <div key={t.id} onClick={() => handleTemplateClick(t)}
                          className="bg-white rounded-lg shadow p-4 cursor-pointer hover:border-blue-500 hover:shadow-md border-2 border-transparent transition-all">
                          <div className="font-medium text-gray-800">{t.name}</div>
                          {t.description && <div className="text-xs text-gray-500 mt-1">{t.description}</div>}
                          {t.has_docx && <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">📄 .docx</span>}
                          <div className="mt-2 text-xs text-blue-600">Click to merge →</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {availableTemplates.length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  No templates available. Ask an admin to add templates in the Template Library.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Merged document preview screen
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex flex-col">
      <div className="bg-white flex items-center justify-between px-4 py-2 border-b shadow">
        <h2 className="font-bold text-lg" style={{color:'#1a2744'}}>📄 {selectedTemplate.name} — Merge Preview</h2>
        <div className="flex gap-2">
          <button onClick={() => setSelectedTemplate(null)} className="px-3 py-1.5 text-sm bg-gray-200 rounded hover:bg-gray-300">← Templates</button>
          <button onClick={handlePrint} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">🖨️ Print</button>
          <button onClick={onClose} className="px-3 py-1.5 text-sm bg-gray-200 rounded hover:bg-gray-300">✕ Close</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
        {loading ? (
          <div className="text-center text-gray-500 mt-20">Loading merge data...</div>
        ) : (
          <div className="bg-white shadow-lg mx-auto max-w-4xl">
            <div
              contentEditable
              suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: html }}
              className="outline-none p-8"
              style={{ minHeight: '600px' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
